/**
 * Employee Skill API Contract
 *
 * Mirrors: backend/src/routes/employeeSkill.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateEmployeeSkillInputSchema, UpdateEmployeeSkillInputSchema, EmployeeSkillResponseSchema, } from "@hourly/api/skills";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Employee Skill Contracts
// ============================================
/**
 * POST /api/employee-skills
 * Assign a skill to an employee
 */
export const createEmployeeSkill = oc
    .route({
    method: "POST",
    path: "/employee-skills",
    summary: "Assign a skill to an employee",
    tags: ["Employee Skills"],
    successStatus: 201,
})
    .input(CreateEmployeeSkillInputSchema)
    .output(EmployeeSkillResponseSchema);
/**
 * GET /api/employee-skills/:id
 * Get an employee skill by ID
 */
export const getEmployeeSkill = oc
    .route({
    method: "GET",
    path: "/employee-skills/{id}",
    summary: "Get employee skill by ID",
    tags: ["Employee Skills"],
})
    .input(z.object({ id: uuidSchema }))
    .output(EmployeeSkillResponseSchema);
/**
 * GET /api/employee-skills/employee/:employeeId
 * Get all skills for an employee
 */
export const getEmployeeSkillsByEmployee = oc
    .route({
    method: "GET",
    path: "/employee-skills/employee/{employeeId}",
    summary: "Get all skills for an employee",
    tags: ["Employee Skills"],
})
    .input(z.object({ employeeId: uuidSchema }))
    .output(z.array(EmployeeSkillResponseSchema));
/**
 * GET /api/employee-skills/skill/:baseSkillId
 * Get all employees with a specific skill
 */
export const getEmployeesBySkill = oc
    .route({
    method: "GET",
    path: "/employee-skills/skill/{baseSkillId}",
    summary: "Get all employees with a specific skill",
    tags: ["Employee Skills"],
})
    .input(z.object({ baseSkillId: uuidSchema }))
    .output(z.array(EmployeeSkillResponseSchema));
/**
 * GET /api/employee-skills/expiring?days=N
 * Get expiring certifications
 */
export const getExpiringCertifications = oc
    .route({
    method: "GET",
    path: "/employee-skills/expiring",
    summary: "Get expiring certifications",
    tags: ["Employee Skills"],
})
    .input(z.object({
    days: z.coerce.number().int().nonnegative().optional().default(30),
}))
    .output(z.array(EmployeeSkillResponseSchema));
/**
 * PATCH /api/employee-skills/:id
 * Update an employee skill
 */
export const updateEmployeeSkill = oc
    .route({
    method: "PATCH",
    path: "/employee-skills/{id}",
    summary: "Update an employee skill",
    tags: ["Employee Skills"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateEmployeeSkillInputSchema))
    .output(EmployeeSkillResponseSchema);
/**
 * DELETE /api/employee-skills/:id
 * Remove a skill from an employee
 */
export const deleteEmployeeSkill = oc
    .route({
    method: "DELETE",
    path: "/employee-skills/{id}",
    summary: "Remove a skill from an employee",
    tags: ["Employee Skills"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// ============================================
// Contract Router
// ============================================
export const employeeSkillContract = {
    createEmployeeSkill,
    getEmployeeSkill,
    getEmployeeSkillsByEmployee,
    getEmployeesBySkill,
    getExpiringCertifications,
    updateEmployeeSkill,
    deleteEmployeeSkill,
};
//# sourceMappingURL=employeeSkill.js.map